import { create } from 'zustand';

import {
	extractAdditionalLabels,
	extractCoreLabels
} from 'src/apps/dashboard/helpers/ConfigurationProcessingHelpers';
import {
	DEFAULT_UNIT_ID_DISPLAY_NAME,
	UNIT_ID_LABEL_KEY
} from 'src/common/consts/platformConfigConsts';
import { Logger } from 'src/common/services/Logger';
import { ConfigurationLabel, PlatformConfiguration } from 'src/common/types/ConfigurationTypes';

interface PlatformConfigState {
	coreLabels: ConfigurationLabel[];
	additionalLabels: ConfigurationLabel[];
	tenantConfiguredLabels: ConfigurationLabel[];
	isNewPlatformEnabled: boolean;
	unitIdDisplayName: string;
	unitIdLabelConfig: ConfigurationLabel | undefined;
	isInitialized: boolean;

	// Actions
	initialize: (platform: PlatformConfiguration) => void;
	reset: () => void;
}

const getUnitIdDisplayName = (unitIdLabel?: ConfigurationLabel): string => {
	if (
		unitIdLabel &&
		typeof unitIdLabel.displayName === 'string' &&
		unitIdLabel.displayName.trim().length > 0
	) {
		return unitIdLabel.displayName;
	}
	return DEFAULT_UNIT_ID_DISPLAY_NAME;
};

const initialState = {
	coreLabels: [],
	additionalLabels: [],
	tenantConfiguredLabels: [],
	isNewPlatformEnabled: false,
	unitIdDisplayName: DEFAULT_UNIT_ID_DISPLAY_NAME,
	unitIdLabelConfig: undefined,
	isInitialized: false
};

export const usePlatformConfigStore = create<PlatformConfigState>((set) => ({
	...initialState,

	initialize: (platform: PlatformConfiguration) => {
		try {
			const coreLabels = extractCoreLabels(platform);
			const additionalLabels = extractAdditionalLabels(platform);
			const tenantConfiguredLabels = [...coreLabels, ...additionalLabels];
			const isNewPlatformEnabled = platform.DEV_TOGGLES?.ENABLE_NEW_PLATFORM_DASHBOARD ?? false;

			const unitIdLabel = coreLabels.find((label) => label.label === UNIT_ID_LABEL_KEY);
			const unitIdDisplayName = getUnitIdDisplayName(unitIdLabel);

			set({
				coreLabels,
				additionalLabels,
				tenantConfiguredLabels,
				isNewPlatformEnabled,
				unitIdDisplayName,
				unitIdLabelConfig: unitIdLabel,
				isInitialized: true
			});
		} catch (error) {
			Logger.error('Failed to initialize platform config store:', error);
			set({
				...initialState,
				isInitialized: true
			});
		}
	},

	reset: () => set(initialState)
}));
